import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Dropdown, Modal, message } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useRecoilState } from "recoil";
import { brandKitsState } from "../../../../state/atoms";
import AssetApi from "../../../../api/AssetApi";
import { AssetDto } from "../../../../generated/dashboard-client";
import "./AddToBrandKitModal.scss";

interface AddToBrandKitModalProps {
  open: boolean;
  currentBrandKitId: string;
  currentBrandKitName?: string;
  assets: AssetDto[];
  onClose: () => void;
  onAdded?: (kitNames: string[]) => void;
}

export function AddToBrandKitModal({
  open,
  currentBrandKitId,
  currentBrandKitName,
  assets,
  onClose,
  onAdded,
}: AddToBrandKitModalProps) {
  const [brandKits, setBrandKits] = useRecoilState(brandKitsState);
  const [selected, setSelected] = useState<string[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [dropdownWidth, setDropdownWidth] = useState<number>();

  useEffect(() => {
    if (open) {
      setSelected([]);
      setDropdownOpen(false);
    }
  }, [open]);

  const total = assets.length;
  const assetIds = useMemo(
    () => assets.map((a) => a.id!).filter(Boolean),
    [assets]
  );

  const otherKits = useMemo(
    () => brandKits.filter((b) => b.id && b.id !== currentBrandKitId),
    [brandKits, currentBrandKitId]
  );

  const alreadyCountFor = (name?: string) =>
    name ? assets.filter((a) => a.brandKitNames?.includes(name)).length : 0;

  function toggle(id: string) {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const selectedNames = otherKits
    .filter((b) => selected.includes(b.id!))
    .map((b) => b.name || "Untitled");

  function close() {
    if (adding) return;
    onClose();
  }

  function handleAdd() {
    if (selected.length === 0 || assetIds.length === 0) return;
    setAdding(true);

    const targets = otherKits.filter((b) => selected.includes(b.id!));

    AssetApi.assignToBrands(assetIds, targets.map((b) => b.id!))
      .then(() => {
        setBrandKits((prev) =>
          prev.map((b) => {
            if (!selected.includes(b.id!)) return b;
            const added = total - alreadyCountFor(b.name);
            return added > 0
              ? ({ ...b, assetCount: ((b as any).assetCount ?? 0) + added } as any)
              : b;
          })
        );
        const names = targets.map((b) => b.name || "Untitled");
        const noun = `asset${total === 1 ? "" : "s"}`;
        const kitNoun = `Brand Kit${names.length === 1 ? "" : "s"}`;
        message.success(`${total} ${noun} added to ${names.length} ${kitNoun}`);
        onAdded?.(names);
        onClose();
      })
      .catch(() => message.error("Failed to add to Brand Kit"))
      .finally(() => setAdding(false));
  }

  const dropdownPanel = (
    <div className="add-to-brand-kit-panel" style={{ width: dropdownWidth }}>
      <div className="add-to-brand-kit-option current" key="current">
        <span className="add-to-brand-kit-option-label">
          <Checkbox checked disabled />
          <span className="add-to-brand-kit-option-name">
            {currentBrandKitName || "Current Brand Kit"}
          </span>
        </span>
        <span className="add-to-brand-kit-current-tag">Current kit</span>
      </div>
      {otherKits.map((b) => {
        const already = alreadyCountFor(b.name);
        const allHere = total > 0 && already === total;
        return (
          <div
            className="add-to-brand-kit-option"
            key={b.id}
            onClick={() => !allHere && toggle(b.id!)}
          >
            <span className="add-to-brand-kit-option-label">
              <Checkbox
                checked={allHere || selected.includes(b.id!)}
                disabled={allHere}
              />
              <span className="add-to-brand-kit-option-name">
                {b.name || "Untitled"}
              </span>
            </span>
            {allHere ? (
              <span className="add-to-brand-kit-badge">Already here</span>
            ) : already > 0 ? (
              <span className="add-to-brand-kit-badge">
                {already} of {total} already here
              </span>
            ) : null}
          </div>
        );
      })}
      {otherKits.length === 0 && (
        <div className="add-to-brand-kit-empty">No other Brand Kits</div>
      )}
    </div>
  );

  return (
    <Modal
      className="add-to-brand-kit-modal"
      open={open}
      onCancel={close}
      footer={null}
      width={600}
      destroyOnClose
    >
      <header>
        <h2>Add to other Brand Kit</h2>
        <p>
          Adding {total} asset{total === 1 ? "" : "s"} to another Brand Kit.
          <br />
          They'll stay in {currentBrandKitName || "this Brand Kit"} too.
        </p>
      </header>

      <Dropdown
        overlay={dropdownPanel}
        trigger={["click"]}
        visible={dropdownOpen}
        onVisibleChange={(v) => {
          if (v && triggerRef.current) {
            setDropdownWidth(triggerRef.current.offsetWidth);
          }
          setDropdownOpen(v);
        }}
        overlayClassName="add-to-brand-kit-dropdown"
        placement="bottomLeft"
      >
        <div
          ref={triggerRef}
          className={`add-to-brand-kit-trigger${dropdownOpen ? " open" : ""}`}
        >
          <span
            className={`add-to-brand-kit-trigger-text${
              selectedNames.length ? "" : " placeholder"
            }`}
          >
            {selectedNames.length ? selectedNames.join(", ") : "Select Brand Kit"}
          </span>
          <DownOutlined />
        </div>
      </Dropdown>

      <footer>
        <Button size="large" onClick={close} disabled={adding}>
          Cancel
        </Button>
        <Button
          size="large"
          className="primary"
          onClick={handleAdd}
          loading={adding}
          disabled={selected.length === 0}
        >
          Add
        </Button>
      </footer>
    </Modal>
  );
}
